import { apiRequest } from "@/lib/api";
const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";

export type PublicPricingCategory = "labor" | "materials" | "equipment" | "subcontractor" | "other";
export type PublicPricingUnit =
  | "hrs"
  | "ea"
  | "box"
  | "bag"
  | "sheet"
  | "bucket"
  | "roll"
  | "ft"
  | "lf"
  | "sq_ft"
  | "gal"
  | "cu_yd"
  | "lb"
  | "day"
  | "fixed"
  | "other";
export type PublicPricingMode = "simple_total" | "internal_estimate";
export type PublicCustomerDisplayMode =
  | "final_price_only"
  | "summary_breakdown"
  | "full_itemized_breakdown";

export type PublicPricingMetadata = {
  pricingVersion: 1;
  pricingMode: PublicPricingMode;
  customerDisplayMode: PublicCustomerDisplayMode;
  customerPrice: number;
  discountAmount: number;
  taxAmount: number;
  finalAmount: number;
  changeOrder?: {
    reasonForChange?: string;
    reasonForChangeLabel?: string;
    changeType?: string;
    changeTypeLabel?: string;
  };
  extraWork?: {
    reason?: string;
    reasonLabel?: string;
  };
  internalEstimate?: {
    rows: Array<{
      id: string;
      category: PublicPricingCategory;
      description: string;
      quantity: number;
      unit: PublicPricingUnit;
      unitCost: number;
      lineTotal: number;
      thumbnailPath?: string | null;
      thumbnailUrl?: string | null;
      visibleToCustomer: boolean;
    }>;
    internalSubtotal: number;
    suggestedCustomerPrice: number;
    customerFacingFinalPrice: number;
  } | null;
};

export type PublicApprovalView = {
  id: string;
  ticketId: string;
  ticketNumber: string;
  ticketType: "CHANGE_ORDER" | "TM_TICKET" | "EXTRA_WORK";
  ticketStatus: string;
  title: string;
  description?: string | null;
  showDescriptionToCustomer?: boolean;
  showAttachmentsToCustomer?: boolean;
  decision?: string | null;
  decisionComment?: string | null;
  decidedAt?: string | null;
  sentAt?: string | null;
  tokenExpiresAt: string;
  recipientName: string;
  recipientEmail: string;
  contractor: { name: string };
  project?: { id: string; name: string; jobSiteAddress?: string | null } | null;
  client?: { id: string; name: string } | null;
  subtotal: number;
  tax: number;
  total: number;
  pricingMetadata?: PublicPricingMetadata | null;
  projectFinancialContext?: {
    showRollup: boolean;
    mode?: "CHANGE_ORDER" | "EXTRA_WORK";
    approvedEstimateTotal: number;
    approvedChangeOrderDeltaTotal: number;
    approvedExtraWorkTotal: number;
    previousApprovedTotal: number;
    currentTicketTotal: number;
    revisedProjectTotal: number;
    previousApprovedTickets: Array<{
      id: string;
      ticketNumber: string;
      title: string;
      type: "CHANGE_ORDER" | "TM_TICKET" | "EXTRA_WORK";
      totalAmount: number;
      approvedAt: string;
    }>;
  };
  lineItems: Array<{
    id: string;
    direction?: "ADD" | "DEDUCT";
    category: string;
    description: string;
    thumbnailPath?: string | null;
    thumbnailUrl?: string | null;
    quantity: number;
    unit: string;
    unitCost: number;
    totalCost: number;
  }>;
  attachments: Array<{
    id: string;
    fileName: string;
    mimeType: string;
    storagePath: string;
    tag: string;
    createdAt: string;
    url: string;
  }>;
  signature?: {
    id: string;
    signerName: string;
    signerTitle?: string | null;
    signerCompany?: string | null;
    signaturePath: string;
    signedAt: string;
  } | null;
  requireSignatureToApprove: boolean;
  clarificationThread?: Array<{
    id: string;
    eventType: string;
    authorType: "CUSTOMER" | "INTERNAL" | "SYSTEM";
    authorName?: string | null;
    authorEmail?: string | null;
    message?: string | null;
    createdAt: string;
  }>;
  latestCompanyResponse?: {
    message?: string | null;
    createdAt: string;
    authorName?: string | null;
    authorEmail?: string | null;
  } | null;
  referenceNumber: string;
};

export function fetchPublicApproval(token: string) {
  return apiRequest<PublicApprovalView>(`/public/approvals/${token}`, {
    method: "GET",
    auth: false,
  });
}

export function submitPublicApprovalDecision(
  token: string,
  payload: {
    decision: "APPROVED" | "REJECTED" | "CLARIFICATION_REQUESTED";
    signerName: string;
    signerTitle?: string;
    signerCompany?: string;
    comment?: string;
    signatureDataUrl?: string;
  },
) {
  return apiRequest<{
    success: boolean;
    decision: "APPROVED" | "REJECTED" | "CLARIFICATION_REQUESTED";
    decidedAt: string;
    referenceNumber: string;
  }>(`/public/approvals/${token}/decision`, {
    method: "POST",
    auth: false,
    body: payload,
  });
}

export async function downloadPublicApprovalPdf(token: string) {
  const response = await fetch(`${API_BASE}/public/approvals/${token}/pdf`, {
    method: "GET",
  });
  if (!response.ok) {
    let message = `Download failed (${response.status})`;
    try {
      const payload = await response.json();
      message = payload?.message ?? payload?.error ?? message;
    } catch {
      // no-op
    }
    throw new Error(Array.isArray(message) ? message.join(", ") : message);
  }

  const disposition = response.headers.get("content-disposition") ?? "";
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const fileName = match?.[1] ?? `workscope-${token}.pdf`;
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
